import React, { SyntheticEvent } from 'react';
import { Cell, ColumnInterface } from 'react-table';

import { detectionIsOn, getDetectionStatusUpdate, locale } from '@trqk/utils';
import { Toggle } from '@trqk/components';

import { useToast } from '../../../../../../hooks';
import { getUpdateWarnings } from '../../../helpers';
import { MaxDetectionsExceeded } from '../cards';

interface ToggleDetectionStatusCellProps {
    cell: Cell<DetectionTrack>;
    column: ToggleDetectionStatusColumn;
}

interface ToggleDetectionStatusColumn extends ColumnInterface<DetectionTrack> {
    height: number;
    updateData(nextData: DetectionTrack[]): void;
    data: DetectionTrack[];
    subscriptionDetectionTracks: number;
}

/**
 * Displays a Toggle component that can update the state of a trqkSong.detection_history in a table.
 * @param {ToggleDetectionStatusCellProps} props
 */
export const ToggleDetectionStatusCell = ({
    cell,
    column,
}: ToggleDetectionStatusCellProps): JSX.Element => {
    const toast = useToast();
    const {
        value: { detectionHistory, detectionStatus, trqkAudioId },
    } = cell;


    const enabled = detectionIsOn(detectionStatus);
    const { data, height, updateData, subscriptionDetectionTracks } = column;

    const checkedLabel = (
        <div className="text-secondary-500 font-semibold">
            {locale.enUS.COMPONENTS.TABLE.TOGGLE_CELL.ON}
        </div>
    );
    const notCheckedLabel = (
        <div className="text-tertiary-300 font-semibold">
            {locale.enUS.COMPONENTS.TABLE.TOGGLE_CELL.OFF}
        </div>
    );

    /**
     * Determine whether to add a DetectionStatusChange to the table data for this track. We don't
     * add a status change if the new status would be the same as the most recent saved status
     * change.
     * @param {boolean} nextEnabled
     */
    const handleChange = (nextEnabled: boolean): void => {
        if (trqkAudioId !== null) {
            const currentHistory: ManageDetectionsStatusChange[] =
                detectionHistory ?? [];
            const nextHistory = [...currentHistory];

            const nextStatus = nextEnabled
                ? ('ENABLED' as DetectionStatus)
                : ('DISABLED' as DetectionStatus);

            const lastPermanentClientStatusChange = getDetectionStatusUpdate({
                detectionHistory: currentHistory,
                nonDraftOnly: true,
            });

            const statusWillChange = nextStatus !== detectionStatus;

            // Create a draft version of the status change
            const nextDetectionStatusChange = {
                draft: true,
                status: nextStatus,
                source: 'CLIENT' as DetectionStatusChangeSource,
                timestamp: new Date().toISOString(),
            };

            const lastStatusChange =
                currentHistory[currentHistory.length - 1] ?? null;

            // If there was already a draft, because the user has toggled the switch back and
            // forth, then remove the prior draft
            if (lastStatusChange?.draft ?? false) {
                nextHistory.pop();
            }

            // If:
            // 1. this draft would change the status as compared to the most recent
            // trqkAudio.detection_history status change that was not a draft, or
            // 2. the trqkAudio.detection_status is different than the next status,
            //      add the next status change to the history
            if (
                (nextStatus !== lastPermanentClientStatusChange?.status ??
                    null) ||
                statusWillChange
            ) {
                nextHistory.push(nextDetectionStatusChange);
            }

            // Assemble next state of the data. Using `findIndex` to find the index of the row is
            // more efficient than doing .map because we don't have to iterate over the entire array
            // if the row is found early on.
            const editIndex = data.findIndex(
                currentRow => currentRow.audio_id === trqkAudioId,
            );

            const nextData = [...data];

            if (editIndex > -1) {
                const oldRow = data[editIndex];

                nextData[editIndex] = {
                    ...oldRow,
                    detection_history: nextHistory,
                    detection_status: nextEnabled
                        ? ('ENABLED' as DetectionStatus)
                        : ('DISABLED' as DetectionStatus),
                };
            }

            const updateWarnings = getUpdateWarnings({
                data: nextData,
                maxDetections: subscriptionDetectionTracks,
                nextStatus,
            });

            // If the next state would exceed the max detection limit, warn the that user they
            // can't make this change
            if (updateWarnings.exceededMaxDetections) {
                toast.add({
                    kind: 'warning-card',
                    RenderContent: MaxDetectionsExceeded,
                    persist: true,
                    renderContentInput: {
                        showTrackSpecificMessage: true,
                        maxDetections: subscriptionDetectionTracks,
                    },
                });
            } else {
                // Otherwise, update the table data with the next data state to reflect the
                // future update
                updateData(nextData);
            }
        }
    };

    return (
        <div
            className="pl-4 border-l border-tertiary-100 flex items-center"
            style={{ height }}
        >
            <Toggle
                size="large"
                inputId={`${trqkAudioId}-toggle`}
                checked={enabled}
                onChange={(e: SyntheticEvent): void => {
                    const target = e.target as HTMLInputElement;
                    const nextEnabled = target?.checked ?? false;

                    handleChange(nextEnabled);
                }}
                label={enabled ? checkedLabel : notCheckedLabel}
            />
        </div>
    );
};
